/**
 * Age filter for the collection grid.
 *
 * Membership comes from the age collections (rendered into #AgeFacetData as a
 * product-handle map), not from tags. Liquid cannot read query parameters, so
 * the page ships every product and this narrows the grid in place for `?age=`.
 *
 * It has to co-operate with Dawn's facet JS, which re-renders the grid and
 * rewrites the URL for Availability/Price without knowing about `age`:
 *  - re-applies after every grid mutation, before the next paint
 *  - keeps `age` on the facet forms and per-filter links, but never on
 *    "Remove all", which must clear the age filter too
 *  - handles its own chip/option clicks ahead of Dawn's <facet-remove> handler
 *  - writes history entries in the shape Dawn's back/forward handler expects
 *  - closes the desktop Age dropdown after a choice (it is single-select)
 */
(function () {
  var PARAM = 'age';
  var dataEl = document.getElementById('AgeFacetData');
  if (!dataEl) return;

  var data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  if (!data || !data.options || !data.options.length) return;

  var byValue = {};
  data.options.forEach(function (option) {
    byValue[option.value] = option;
  });

  function currentValue() {
    var value = new URLSearchParams(window.location.search).get(PARAM);
    return value && byValue[value] ? value : null;
  }

  function gridItems() {
    var grid = document.getElementById('product-grid');
    return grid ? Array.prototype.slice.call(grid.children) : [];
  }

  /** Grid items carry no product id, so identify them by their product link. */
  function handleOf(item) {
    var link = item.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function applyFilter() {
    var value = currentValue();
    var items = gridItems();
    if (!items.length) return 0;

    var allowed = value ? byValue[value].handles : null;
    var shown = 0;

    items.forEach(function (item) {
      if (!allowed) {
        item.hidden = false;
        shown++;
        return;
      }
      var handle = handleOf(item);
      var match = handle !== null && allowed.indexOf(handle) !== -1;
      item.hidden = !match;
      if (match) shown++;
    });

    updateCount(shown, !!value);
    updateOptionStates(value);
    updateChip(value);
    updateEmptyState(shown, value);
    return shown;
  }

  /**
   * Dawn rewrites the count on every re-render, so remember the server's text
   * whenever it differs from what we last wrote — otherwise clearing the age
   * filter would restore a count from before an Availability/Price change.
   */
  function updateCount(shown, filtered) {
    ['ProductCount', 'ProductCountDesktop'].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      var text = el.textContent.trim();
      if (!filtered) {
        if (el.dataset.ageOriginal && text === el.dataset.ageShown) el.textContent = el.dataset.ageOriginal;
        delete el.dataset.ageOriginal;
        delete el.dataset.ageShown;
        return;
      }
      if (text !== el.dataset.ageShown) el.dataset.ageOriginal = text;
      var shownText = shown === 1 ? '1 product' : shown + ' products';
      el.textContent = shownText;
      el.dataset.ageShown = shownText;
    });
  }

  function updateOptionStates(value) {
    document.querySelectorAll('[data-facet-age-link]').forEach(function (link) {
      var isActive = link.dataset.ageValue === value;
      link.classList.toggle('active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
      // Clicking the active option clears it.
      link.setAttribute('href', urlFor(isActive ? null : link.dataset.ageValue));
    });
  }

  /** Build a URL that keeps every other facet/sort param intact. */
  function urlFor(value) {
    return withAge(window.location.pathname + window.location.search, value);
  }

  /** Set or drop `age` on a path+query, leaving the rest of the query alone. */
  function withAge(href, value) {
    var url = new URL(href, window.location.origin);
    if (value) {
      url.searchParams.set(PARAM, value);
    } else {
      url.searchParams.delete(PARAM);
    }
    var query = url.searchParams.toString();
    return url.pathname + (query ? '?' + query : '');
  }

  function updateChip(value) {
    document.querySelectorAll('[data-age-chip]').forEach(function (chip) {
      chip.remove();
    });
    if (!value) return;

    document.querySelectorAll('.active-facets').forEach(function (container) {
      // Parsed from a <template> so <facet-remove> upgrades only once its link
      // exists — created empty, Dawn's constructor throws on the missing <a>.
      var template = document.createElement('template');
      template.innerHTML =
        '<facet-remove data-age-chip>' +
        '<a class="active-facets__button active-facets__button--light" data-facet-age-clear>' +
        '<span class="active-facets__button-inner button button--tertiary"></span>' +
        '</a></facet-remove>';
      var chip = template.content.firstElementChild;
      var link = chip.querySelector('a');
      link.setAttribute('href', urlFor(null));
      link.firstElementChild.textContent = data.label + ': ' + byValue[value].label + ' ✕';

      // Sit before "Remove all" so the row reads like the built-in chips. In the
      // vertical layout "Remove all" is nested deeper, so fall back to appending.
      var clearAll = container.querySelector('.active-facets__button-wrapper');
      if (clearAll && clearAll.parentNode === container) {
        container.insertBefore(chip, clearAll);
      } else {
        container.appendChild(chip);
      }
      container.classList.remove('hidden');
    });
  }

  function updateEmptyState(shown, value) {
    var existing = document.querySelector('[data-age-empty]');
    if (shown > 0 || !value) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var message = document.createElement('p');
    message.setAttribute('data-age-empty', '');
    message.className = 'facet-age__empty';
    message.textContent = 'No products match ' + data.label + ': ' + byValue[value].label + '.';
    grid.parentNode.insertBefore(message, grid.nextSibling);
  }

  /**
   * Dawn's facet links/forms are built from the current path and its filter.*
   * params only, so `age` would be dropped on the next Availability/Price
   * change. Keep it in step with the URL — set when active, removed when not —
   * on the forms and per-filter links, but leave "Remove all" alone.
   */
  function preserveParam() {
    var value = currentValue();

    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile').forEach(function (form) {
      var field = form.querySelector('input[name="' + PARAM + '"]');
      if (!value) {
        if (field) field.remove();
        return;
      }
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = PARAM;
        form.appendChild(field);
      }
      field.value = value;
    });

    document
      .querySelectorAll(
        '.active-facets a[href]:not([data-facet-age-clear]):not(.active-facets__button-remove), .facets__reset'
      )
      .forEach(function (link) {
        var href = link.getAttribute('href');
        if (!href) return;
        var has = new URL(href, window.location.origin).searchParams.get(PARAM);
        if (has === value || (!has && !value)) return;
        link.setAttribute('href', withAge(href, value));
      });
  }

  function sync() {
    applyFilter();
    preserveParam();
  }

  /** Push a history entry in the shape Dawn's popstate handler reads. */
  function navigate(href) {
    var url = new URL(href, window.location.origin);
    var query = url.searchParams.toString();
    window.history.pushState({ searchParams: query }, '', url.pathname + (query ? '?' + query : ''));
    if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = query;
    sync();
  }

  function isAgeControl(target) {
    return target && target.closest ? target.closest('[data-facet-age-link], [data-facet-age-clear]') : null;
  }

  // Capture phase, so this runs before Dawn's <facet-remove> link listener —
  // otherwise both handle the chip and race to rewrite the page and URL.
  document.addEventListener(
    'click',
    function (event) {
      var link = isAgeControl(event.target);
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate(link.getAttribute('href'));
      var drawer = link.closest('.mobile-facets__wrapper');
      if (drawer) {
        var summary = drawer.querySelector('summary');
        if (summary) summary.click();
        return;
      }
      // Age is single-choice, so close the desktop dropdown once picked. Left open,
      // Dawn's full-screen click-catcher swallows the next click (e.g. "Remove all").
      var popup = link.closest('details.disclosure-has-popup[open]');
      if (popup) {
        popup.removeAttribute('open');
        var popupSummary = popup.querySelector('summary');
        if (popupSummary) popupSummary.setAttribute('aria-expanded', 'false');
      }
    },
    true
  );

  // Dawn gives chip links role="button" and activates them on Space via keyup.
  document.addEventListener(
    'keyup',
    function (event) {
      if (!event.code || event.code.toUpperCase() !== 'SPACE') return;
      var link = isAgeControl(event.target);
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate(link.getAttribute('href'));
    },
    true
  );

  window.addEventListener('popstate', sync);

  // Dawn swaps #ProductGridContainer's innerHTML on every facet change. Mutation
  // callbacks run before the next paint, so re-filtering here means the full
  // grid never flashes; takeRecords() drops the mutations sync() itself made.
  var container = document.getElementById('ProductGridContainer');
  if (container && window.MutationObserver) {
    var observer = new MutationObserver(function () {
      sync();
      observer.takeRecords();
    });
    observer.observe(container, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
